import type { ReactNode } from 'react';
import type { Creneau } from '../../partage/api/types';
import { APRES_MINUIT, changerLHeureDe, FAITE, FIXEE, IMPOSE, libreDurant } from '../../partage/vocabulaire';
import { dureeLisible, heureLisible } from './datesDeLAgenda';
import { ChampDHeure } from './PreparerUnJour';

/**
 * La journée qu'on compose au téléphone, ligne à ligne (chantier `telephone-journee-agenda`,
 * tickets 02 et 03) : chaque tâche à son heure, qu'on touche pour la fixer, ce qu'elle est, et ses
 * gestes (× ; ↻ dans « Propose-moi ») ; le rendez-vous du jour à la sienne, sur l'ambre, sans geste ;
 * le temps libre entre deux. Les lignes reprennent celles de « Préparer ma journée ».
 */
export function CreneauxDeLaJournee({
  creneaux,
  enHeure,
  onOuvrirLHeure,
  onQuitterLHeure,
  onFixer,
  quoi,
  gestes,
  faites,
}: {
  creneaux: readonly Creneau[];
  /** La tâche dont le champ d'heure est ouvert. */
  enHeure: string | null;
  onOuvrirLHeure: (id: string) => void;
  onQuitterLHeure: () => void;
  /** Fixer l'heure d'une tâche, ou la remettre à la suite (`null`). */
  onFixer: (id: string, heure: string | null) => void;
  /** Ce que dit la ligne d'une tâche sous son titre. */
  quoi: (c: Creneau) => string;
  gestes: (c: Creneau) => ReactNode;
  /**
   * Les tâches déjà faites (« Changer ma journée », ticket 04) : à leur place, barrées, « Faite »,
   * leur heure tenue, sans geste.
   */
  faites?: ReadonlySet<string>;
}) {
  return (
    <ul className="propose-journee">
      {creneaux.map((c) => {
        const libre =
          c.libreApres && c.libreApres > 0 ? (
            <li key={`libre-${c.sorte}-${c.id}`} className="preparer-libre">
              <span className="preparer-libre-points" aria-hidden="true">
                · · ·
              </span>
              {libreDurant(dureeLisible(c.libreApres))}
            </li>
          ) : null;
        if (c.sorte === 'rendez_vous') {
          return [
            <li key={`rdv-${c.id}`} className="preparer-ligne preparer-ligne--rdv" data-rdv={c.id}>
              <span className="preparer-quand preparer-quand--rdv">{heureLisible(c.heure)}</span>
              <span className="preparer-quoi">
                <b>{c.titre}</b>
                <small>{[IMPOSE, c.minutes > 0 ? dureeLisible(c.minutes) : null].filter(Boolean).join(' · ')}</small>
              </span>
            </li>,
            libre,
          ];
        }
        const faite = !!faites?.has(c.id);
        return [
          <li
            key={`tache-${c.id}`}
            className={`preparer-ligne${faite ? ' est-faite' : ''}${c.horsDuJour ? ' est-hors-du-jour' : ''}`}
            data-tache={c.id}
          >
            <span className="preparer-quand">
              {faite ? (
                <span className="preparer-heure preparer-heure--tenue">{heureLisible(c.heure)}</span>
              ) : enHeure === c.id ? (
                <ChampDHeure
                  valeur={c.heure}
                  etiquette={changerLHeureDe(c.titre)}
                  onEcrire={(h) => onFixer(c.id, h)}
                  onQuitter={onQuitterLHeure}
                  aLaSuite={c.fixee ? () => onFixer(c.id, null) : undefined}
                  auFocus
                  memeHeureSurEntree={!c.fixee}
                />
              ) : (
                <button
                  type="button"
                  className={`preparer-heure${c.fixee ? ' est-fixee' : ''}`}
                  aria-label={changerLHeureDe(c.titre)}
                  onClick={() => onOuvrirLHeure(c.id)}
                >
                  {c.horsDuJour ? APRES_MINUIT : heureLisible(c.heure)}
                </button>
              )}
              {faite ? (
                <span className="preparer-sorte">{FAITE}</span>
              ) : (
                c.fixee && enHeure !== c.id && <span className="preparer-sorte est-fixee">{FIXEE}</span>
              )}
            </span>
            <span className="preparer-quoi">
              <b>{c.titre}</b>
              <small>{quoi(c)}</small>
            </span>
            {!faite && gestes(c)}
          </li>,
          libre,
        ];
      })}
    </ul>
  );
}

/** Le geste × d'une ligne : enlever la tâche de la journée. */
export function CroixDuRetrait() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
